/**
 * useUpdatePipelineStages — mutation for the full pipeline stages array.
 *
 * Wraps PUT /api/orgs/<orgId>/crm-events/schemas/pipelines/<pipelineEntityId>/stages/
 * with optimistic update + rollback + cache invalidation.
 *
 * The endpoint accepts the full stages array (rename, reorder, add, delete in
 * one shot). This hook is shared across rename, add, reorder, delete, and
 * recolor operations on the pipeline board.
 *
 * On success, invalidates the `['pipelines', orgId]` and `['stages', orgId]`
 * query caches so the WebSocket-driven and React-Query-driven views stay
 * consistent.
 *
 * On failure, rolls back the optimistic update and surfaces a toast.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { apiPut } from '@/lib/http/client';

import type { Pipeline, PipelineStageSchema } from './usePipelines';

/** Body shape expected by the PUT endpoint for a single stage. */
export interface PipelineStageInput {
  /** Omit for new stages — the server assigns a new entity_id. */
  entity_id?: string;
  /** Display name (renamed). */
  name: string;
  /** For deal pipelines; carry through unchanged for non-deal pipelines. */
  probability?: number;
  /** 0-based position in the pipeline. */
  order: number;
  /** Hex color (or null). */
  color: string | null;
}

interface UpdatePipelineStagesArgs {
  /** Full ordered stages array — replaces the existing list on the server. */
  nextStages: PipelineStageInput[];
  /**
   * Operation-specific copy shown if the request fails. Defaults to a
   * generic message — pass something like "Couldn't rename stage. Try again."
   * for better UX on rename, add, delete, etc.
   */
  errorToast?: string;
}

interface OptimisticContext {
  previous: Pipeline[] | undefined;
  queryKey: readonly unknown[];
  errorToast: string;
}

interface UseUpdatePipelineStagesOptions {
  orgId: number | undefined;
  pipelineEntityId: string | null | undefined;
  /** Entity type the pipeline targets (e.g. 'ahoy_deal'). Used for invalidation key. */
  entityType: string | undefined;
}

const DEFAULT_ERROR_TOAST = "Couldn't update pipeline stages. Try again.";

/**
 * Build the optimistic Pipeline[] cache shape from a list of stage inputs.
 * Preserves untouched fields (stage_name, description) by matching entity_id
 * against the previous pipeline's stages.
 */
function buildOptimisticPipelines(
  previous: Pipeline[],
  pipelineEntityId: string,
  nextStages: PipelineStageInput[]
): Pipeline[] {
  return previous.map(p => {
    if (p.entity_id !== pipelineEntityId) {
      return p;
    }
    const nextSchemas: PipelineStageSchema[] = [...nextStages]
      .sort((a, b) => a.order - b.order)
      .map(s => {
        const existing = s.entity_id
          ? p.stages.find(ps => ps.entity_id === s.entity_id)
          : undefined;
        return {
          stage_name: existing?.stage_name ?? '',
          label: s.name,
          description: existing?.description ?? '',
          entity_id: s.entity_id ?? null,
          is_terminal: existing?.is_terminal ?? false,
          properties: {
            probability: s.probability,
            order: s.order,
            color: s.color,
          },
        };
      });
    return { ...p, stages: nextSchemas };
  });
}

/**
 * Hook for mutating the full pipeline stages array.
 *
 * Returns a mutation object whose `mutate` / `mutateAsync` accepts a
 * `PipelineStageInput[]` representing the desired final state.
 *
 * The mutation is enabled only when `orgId` and `pipelineEntityId` are present.
 * Callers should gate UI affordances on `isPending` / permission checks before
 * invoking the mutation.
 */
export function useUpdatePipelineStages({
  orgId,
  pipelineEntityId,
  entityType,
}: UseUpdatePipelineStagesOptions) {
  const queryClient = useQueryClient();

  return useMutation<
    unknown,
    Error,
    UpdatePipelineStagesArgs,
    OptimisticContext
  >({
    mutationFn: async ({ nextStages }) => {
      if (!orgId || !pipelineEntityId) {
        throw new Error('orgId and pipelineEntityId are required');
      }
      return apiPut(
        `/api/orgs/${orgId}/crm-events/schemas/pipelines/${pipelineEntityId}/stages/`,
        { stages: nextStages, entity_type: entityType }
      );
    },
    onMutate: async ({ nextStages, errorToast }) => {
      const queryKey = ['pipelines', orgId, entityType] as const;

      // Cancel in-flight refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<Pipeline[]>(queryKey);
      if (previous && pipelineEntityId) {
        queryClient.setQueryData<Pipeline[]>(
          queryKey,
          buildOptimisticPipelines(previous, pipelineEntityId, nextStages)
        );
      }

      return {
        previous,
        queryKey,
        errorToast: errorToast ?? DEFAULT_ERROR_TOAST,
      };
    },
    onError: (_err, _vars, context) => {
      // Roll back to the snapshot
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.queryKey, context.previous);
      }
      toast.error(context?.errorToast ?? DEFAULT_ERROR_TOAST);
    },
    onSettled: () => {
      // Always reconcile with the server (handles new entity_ids on add,
      // server-side derived fields, etc.). The WebSocket echo will also
      // keep us fresh, but invalidating ensures a refetch even if events
      // are missed.
      if (orgId !== undefined) {
        void queryClient.invalidateQueries({ queryKey: ['pipelines', orgId] });
        void queryClient.invalidateQueries({ queryKey: ['stages', orgId] });
      }
    },
  });
}
